import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Modal,
  Platform,
} from 'react-native';
import DateTimePicker from '@react-native-community/datetimepicker';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { GlassCard } from '@/components/shared/redesign';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { PermissionChoice } from '@/hooks/residents/useMaintenanceForm';

interface StepAccessProps {
  permissionChoice: PermissionChoice;
  onPermissionChoiceChange: (value: PermissionChoice) => void;
  timeSlots: string[];
  onTimeSlotsChange: (slots: string[]) => void;
  hasPets: boolean;
  onHasPetsChange: (value: boolean) => void;
  specialInstructions: string;
  onSpecialInstructionsChange: (value: string) => void;
}

/**
 * StepAccess — redesign step that merges StepEntryPermission + StepAdditional.
 * Contains: heading, entry-permission Yes/No toggle, time-slot picker (when "No"),
 * pets Yes/No toggle, and special-instructions text input.
 */
export const StepAccess: React.FC<StepAccessProps> = ({
  permissionChoice,
  onPermissionChoiceChange,
  timeSlots,
  onTimeSlotsChange,
  hasPets,
  onHasPetsChange,
  specialInstructions,
  onSpecialInstructionsChange,
}) => {
  const { colors, semantic } = useThemeColors();

  // --- DateTimePicker state (ported verbatim from StepEntryPermission) ---
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [currentSlotIndex, setCurrentSlotIndex] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState(new Date());

  // --- Seed 3 empty slots when switching to "No" ---
  // Intentionally depends only on permissionChoice to avoid re-triggering on every slot change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => { if (permissionChoice === 'no' && timeSlots.length < 3) { onTimeSlotsChange(['', '', '']); } }, [permissionChoice]);

  // --- Entry-permission handlers ---
  const handlePermissionYes = () => {
    onPermissionChoiceChange('yes');
    onTimeSlotsChange([]); // Clear time slots when "Yes"
  };

  const handlePermissionNo = () => {
    onPermissionChoiceChange('no');
  };

  // --- Time-slot picker handlers (ported verbatim from StepEntryPermission) ---
  const handleTimeSlotPress = (index: number) => {
    setCurrentSlotIndex(index);
    setSelectedDate(timeSlots[index] ? new Date(timeSlots[index]) : new Date());
    setShowDatePicker(true);
  };

  const handleDateChange = (event: any, date?: Date) => {
    if (Platform.OS === 'android') {
      setShowDatePicker(false);
    }
    if (date && currentSlotIndex !== null) {
      const newSlots = [...timeSlots];
      newSlots[currentSlotIndex] = date.toISOString();
      onTimeSlotsChange(newSlots);
    }
  };

  const handleDatePickerDone = () => {
    setShowDatePicker(false);
    if (currentSlotIndex !== null) {
      const newSlots = [...timeSlots];
      newSlots[currentSlotIndex] = selectedDate.toISOString();
      onTimeSlotsChange(newSlots);
    }
  };

  const formatTimeSlot = (dateString?: string): string => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return '';
    }
  };

  return (
    <View style={styles.container}>
      {/* Heading */}
      <Text style={[styles.heading, { color: colors.text.primary }]}>Access & details</Text>

      {/* Entry Permission Section */}
      <GlassCard padding={16} style={styles.section}>
        <Text style={[styles.sectionLabel, { color: colors.text.muted }]}>
          Can our technician enter your residence?
        </Text>
        <View style={styles.toggleRow}>
          <TouchableOpacity
            style={[
              styles.toggleButton,
              styles.toggleButtonLeft,
              permissionChoice === 'yes'
                ? { backgroundColor: semantic.interactive.primary, borderColor: semantic.interactive.primary }
                : { backgroundColor: colors.card.bg, borderColor: colors.card.border },
            ]}
            onPress={handlePermissionYes}
            activeOpacity={0.8}
          >
            <Text
              style={[
                styles.toggleText,
                permissionChoice === 'yes'
                  ? styles.toggleTextActive
                  : { color: colors.text.secondary },
              ]}
            >
              Yes
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.toggleButton,
              styles.toggleButtonRight,
              permissionChoice === 'no'
                ? { backgroundColor: semantic.interactive.primary, borderColor: semantic.interactive.primary }
                : { backgroundColor: colors.card.bg, borderColor: colors.card.border },
            ]}
            onPress={handlePermissionNo}
            activeOpacity={0.8}
          >
            <Text
              style={[
                styles.toggleText,
                permissionChoice === 'no'
                  ? styles.toggleTextActive
                  : { color: colors.text.secondary },
              ]}
            >
              No
            </Text>
          </TouchableOpacity>
        </View>
      </GlassCard>

      {/* Time Slots (only when "No") */}
      {permissionChoice === 'no' && (
        <View style={styles.timeSlotsSection}>
          {/* Alert Banner */}
          <View style={[styles.alertBox, { backgroundColor: colors.accent.orangeLight }]}>
            <View style={styles.alertIconCircle}>
              <Text style={styles.alertIconText}>!</Text>
            </View>
            <Text style={[styles.alertText, { color: semantic.status.warning }]}>
              Please select 3 time slots in the next 10 days
            </Text>
          </View>

          {/* Time Slot Fields */}
          {[0, 1, 2].map((index) => (
            <TouchableOpacity
              key={index}
              style={[
                styles.dateTimeField,
                {
                  backgroundColor: colors.card.bg,
                  borderColor: colors.card.border,
                  borderBottomColor: colors.text.primary,
                },
              ]}
              onPress={() => handleTimeSlotPress(index)}
              activeOpacity={0.7}
            >
              <View style={styles.dateTimeContent}>
                <View style={styles.dateTimeTextContainer}>
                  {timeSlots[index] ? (
                    <>
                      <Text style={[styles.dateTimeLabel, { color: colors.text.muted }]}>
                        Date and time
                      </Text>
                      <Text style={[styles.dateTimeValue, { color: colors.text.primary }]}>
                        {formatTimeSlot(timeSlots[index])}
                      </Text>
                    </>
                  ) : (
                    <Text style={[styles.dateTimePlaceholder, { color: colors.text.muted }]}>
                      Date and time
                    </Text>
                  )}
                </View>
                <CalendarIcon borderColor={colors.text.secondary} dotColor={colors.text.secondary} />
              </View>
            </TouchableOpacity>
          ))}
        </View>
      )}

      {/* Pets Section */}
      <GlassCard padding={16} style={styles.section}>
        <Text style={[styles.sectionLabel, { color: colors.text.muted }]}>
          Do you have pets?
        </Text>
        <View style={styles.toggleRow}>
          <TouchableOpacity
            style={[
              styles.toggleButton,
              styles.toggleButtonLeft,
              hasPets
                ? { backgroundColor: semantic.interactive.primary, borderColor: semantic.interactive.primary }
                : { backgroundColor: colors.card.bg, borderColor: colors.card.border },
            ]}
            onPress={() => onHasPetsChange(true)}
            activeOpacity={0.8}
          >
            <Text
              style={[
                styles.toggleText,
                hasPets ? styles.toggleTextActive : { color: colors.text.secondary },
              ]}
            >
              Yes
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.toggleButton,
              styles.toggleButtonRight,
              !hasPets
                ? { backgroundColor: semantic.interactive.primary, borderColor: semantic.interactive.primary }
                : { backgroundColor: colors.card.bg, borderColor: colors.card.border },
            ]}
            onPress={() => onHasPetsChange(false)}
            activeOpacity={0.8}
          >
            <Text
              style={[
                styles.toggleText,
                !hasPets ? styles.toggleTextActive : { color: colors.text.secondary },
              ]}
            >
              No
            </Text>
          </TouchableOpacity>
        </View>
      </GlassCard>

      {/* Special Instructions */}
      <GlassCard padding={16} style={styles.section}>
        <TextInput
          style={[styles.instructionsInput, { color: colors.text.primary }]}
          value={specialInstructions}
          onChangeText={onSpecialInstructionsChange}
          placeholder="Anything else our technician should know?"
          placeholderTextColor={colors.text.dimmer}
          maxLength={500}
          multiline
          numberOfLines={4}
          textAlignVertical="top"
          autoCapitalize="sentences"
        />
      </GlassCard>

      {/* Date Time Picker Modal for iOS (ported verbatim from StepEntryPermission) */}
      {Platform.OS === 'ios' && showDatePicker && (
        <Modal
          visible={showDatePicker}
          transparent={true}
          animationType="slide"
          onRequestClose={() => setShowDatePicker(false)}
        >
          <View style={styles.pickerModalOverlay}>
            <View style={[styles.pickerModalContent, { backgroundColor: colors.bg.modal }]}>
              <View style={[styles.pickerModalHeader, { borderBottomColor: colors.card.border }]}>
                <TouchableOpacity onPress={() => setShowDatePicker(false)}>
                  <Text style={[styles.pickerModalCancel, { color: colors.text.muted }]}>
                    Cancel
                  </Text>
                </TouchableOpacity>
                <Text style={[styles.pickerModalTitle, { color: colors.text.primary }]}>
                  Select Date & Time
                </Text>
                <TouchableOpacity onPress={handleDatePickerDone}>
                  <Text
                    style={[
                      styles.pickerModalDone,
                      { color: semantic.interactive.primary },
                    ]}
                  >
                    Done
                  </Text>
                </TouchableOpacity>
              </View>
              <DateTimePicker
                value={selectedDate}
                mode="datetime"
                display="spinner"
                onChange={(event, date) => {
                  if (date) setSelectedDate(date);
                }}
                minimumDate={new Date()}
                maximumDate={new Date(Date.now() + 10 * 24 * 60 * 60 * 1000)}
                textColor={colors.text.primary}
              />
            </View>
          </View>
        </Modal>
      )}

      {/* Android DateTimePicker (ported verbatim from StepEntryPermission) */}
      {Platform.OS === 'android' && showDatePicker && (
        <DateTimePicker
          value={selectedDate}
          mode="datetime"
          display="default"
          onChange={handleDateChange}
          minimumDate={new Date()}
          maximumDate={new Date(Date.now() + 10 * 24 * 60 * 60 * 1000)}
        />
      )}
    </View>
  );
};

// --- Calendar Icon (inline, no marketplace colors dependency) ---
interface CalendarIconProps {
  borderColor: string;
  dotColor: string;
}

const CalendarIcon: React.FC<CalendarIconProps> = ({ borderColor, dotColor }) => (
  <View style={styles.calendarIcon}>
    <View style={[styles.calendarBody, { borderColor }]}>
      <View style={[styles.calendarTop, { backgroundColor: dotColor }]} />
      <View style={styles.calendarGrid}>
        <View style={[styles.calendarDot, { backgroundColor: dotColor }]} />
        <View style={[styles.calendarDot, { backgroundColor: dotColor }]} />
        <View style={[styles.calendarDot, { backgroundColor: dotColor }]} />
        <View style={[styles.calendarDot, { backgroundColor: dotColor }]} />
      </View>
    </View>
  </View>
);

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: REDESIGN_SPACING.horizontalPadding,
    gap: REDESIGN_SPACING.contentGap,
  },

  heading: {
    ...REDESIGN_TYPOGRAPHY.heading1,
    marginBottom: 4,
  },

  section: {
    gap: 12,
  },

  sectionLabel: {
    ...REDESIGN_TYPOGRAPHY.label,
  },

  // --- Yes/No Toggle ---
  toggleRow: {
    flexDirection: 'row',
    borderRadius: 8,
    overflow: 'hidden',
  },
  toggleButton: {
    flex: 1,
    paddingVertical: 10,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
  },
  toggleButtonLeft: {
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
    borderRightWidth: 0,
  },
  toggleButtonRight: {
    borderTopRightRadius: 8,
    borderBottomRightRadius: 8,
  },
  toggleText: {
    ...REDESIGN_TYPOGRAPHY.body,
    fontWeight: '500',
  },
  toggleTextActive: {
    color: '#FFFFFF',
    fontWeight: '600',
  },

  // --- Time Slots ---
  timeSlotsSection: {
    gap: 16,
  },

  alertBox: {
    borderRadius: 8,
    padding: 14,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  },
  alertIconCircle: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: '#FF7629',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
    flexShrink: 0,
  },
  alertIconText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  alertText: {
    flex: 1,
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 21,
  },

  dateTimeField: {
    borderRadius: 8,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderWidth: 1,
    borderBottomWidth: 2,
  },
  dateTimeContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  dateTimeTextContainer: {
    flex: 1,
    gap: 2,
  },
  dateTimeLabel: {
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 12,
    letterSpacing: 0.15,
  },
  dateTimeValue: {
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 24,
  },
  dateTimePlaceholder: {
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 24,
  },

  // --- Calendar Icon ---
  calendarIcon: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  calendarBody: {
    width: 18,
    height: 18,
    borderWidth: 1.5,
    borderRadius: 3,
    padding: 2,
  },
  calendarTop: {
    width: '100%',
    height: 3,
    marginBottom: 2,
  },
  calendarGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 2,
  },
  calendarDot: {
    width: 3,
    height: 3,
    borderRadius: 1,
  },

  // --- Instructions Input ---
  instructionsInput: {
    ...REDESIGN_TYPOGRAPHY.body,
    minHeight: 100,
    lineHeight: 20,
  },

  // --- iOS Picker Modal ---
  pickerModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  pickerModalContent: {
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingBottom: 20,
  },
  pickerModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
  },
  pickerModalTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  pickerModalCancel: {
    fontSize: 16,
  },
  pickerModalDone: {
    fontSize: 16,
    fontWeight: '600',
  },
});

export default StepAccess;
